import { cva } from "class-variance-authority";
import React from "react";

// Types
import { Option } from "../../../types/Option";

// Utils
import { cn } from "../../../lib/utils";

// Components
import { Checkbox, CheckboxProps } from "../../atoms/Checkbox";
import { Text } from "../../atoms/Text";

const checkboxGroupVariants = cva("flex gap-2", {
	variants: {
		orientation: {
			vertical: "flex-col",
			horizontal: "flex-row",
		},
	},
	defaultVariants: {
		orientation: "vertical",
	},
});

type CheckboxGroupProps<T> = {
	/** Array of checkbox options to display */
	options: Option<T>[];
	/** Currently selected values */
	value: T[];
	/** Callback fired when selection changes */
	onChange: (value: T[]) => void;
	/** Optional class name to apply to root element */
	dangerouslySetClassName?: string;
	/** Unique identifier for the checkbox group */
	id: string;
	/** Size of the checkboxes. Inherits from Checkbox component */
	size?: CheckboxProps["size"];
	/** Whether the entire group is disabled */
	disabled?: boolean;
	/** Layout orientation of the checkboxes */
	orientation?: "vertical" | "horizontal";
	/** Test ID for the checkbox group */
	"data-testid"?: string;
};

/**
 *
 * a group of checkboxes and their labels
 * This component is used outside of a form context. If you need a form-connected checkbox group, use FormCheckboxGroup
 */
const CheckboxGroup = <T,>({
	options,
	value,
	onChange,
	dangerouslySetClassName,
	id,
	size,
	disabled,
	orientation,
	"data-testid": dataTestId,
}: CheckboxGroupProps<T>) => {
	return (
		<div
			className={cn(checkboxGroupVariants({ orientation }), dangerouslySetClassName)}
			role="group"
			aria-disabled={disabled}
			id={id}
			data-testid={dataTestId}
		>
			{options.map((option) => {
				const checkboxId = `${id}-${JSON.stringify(option.value)}`;
				const isDisabled = option.disabled || disabled;
				return (
					<div key={checkboxId} className="flex items-start gap-2">
						<Checkbox
							id={checkboxId}
							checked={value.includes(option.value)}
							disabled={isDisabled}
							size={size}
							onCheckedChange={(checked) =>
								onChange(checked ? [...value, option.value] : value.filter((v) => v !== option.value))
							}
							aria-describedby={option.subtext ? `${checkboxId}-description` : undefined}
						/>
						<label
							htmlFor={checkboxId}
							className={cn(isDisabled ? "cursor-not-allowed" : "cursor-pointer")}
						>
							<Text dangerouslySetClassName={cn(isDisabled && "opacity-disabled")} size={"paragraph-3"}>
								{option.label}
							</Text>
							{option.subtext && (
								<Text
									id={`${checkboxId}-description`}
									dangerouslySetClassName={cn(isDisabled && "opacity-disabled")}
									size={"caption"}
									color="neutral-4"
								>
									{option.subtext}
								</Text>
							)}
						</label>
					</div>
				);
			})}
		</div>
	);
};
CheckboxGroup.displayName = "CheckboxGroup";

export { CheckboxGroup, type CheckboxGroupProps };
